import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import lodash from 'lodash';
import { message, Button, Modal, Tooltip, Icon, Dropdown, Menu, Upload, Spin, Switch, Tabs, Select } from 'antd';
import {
  fetchScreeningPackagesBulk,
  fetchScreeningProviderSettings,
  fetchAccreditationDefinitions,
  createScreeningPackage,
  updateScreeningPackage,
  deleteScreeningPackage,
  createScreeningGroup,
  updateMemberGuidingText,
  updateGroupName,
  updateGroupAttachments,
  exportScreeningConfiguration,
  importScreeningConfiguration,
  clearAllScreeningPackages,
} from '../../../actions/screeningPackages';
import { fetchOrganizationSettingsV2, patchOrganizationSettingsV2 } from '../../../actions/organization';
import PackageEditModal from './PackageEditModal';
import PackagePreviewModal from './PackagePreviewModal';
import NewIcrFormNotice from './NewIcrFormNotice';
import ScreeningGroupBrowser from './ScreeningGroupBrowser';

class ScreeningPackageSettingsPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedPackage: null,
      selectedPackageGroups: [],
      showModal: false,
      previewPackage: null,
      previewPackageGroups: [],
      showImportModal: false,
      importFile: null,
      importPreview: null,
      importLoading: false,
      sortColumn: 'name',
      sortDirection: 'asc',
      isInitialLoad: true, // Track if this is the first load vs background refresh
      showIcrNotice: false,
      activeTab: 'packages',
      equivalencyRules: [],
      equivalencyDirty: false,
      equivalencySaving: false,
      showEquivFixedSave: false,
    };

    this.equivHeaderRef = React.createRef();

    // Cache for memoized sorted packages
    this.sortedPackagesCache = {
      packages: null,
      sortColumn: null,
      sortDirection: null,
      result: null,
    };
  }

  componentDidMount() {
    const { dispatch, organizationId } = this.props;

    // Don't fetch if organizationId is not available yet
    if (!organizationId) {
      return;
    }

    // Fetch screening provider settings to check if feature is enabled
    dispatch(fetchScreeningProviderSettings(organizationId));
    dispatch(fetchOrganizationSettingsV2(organizationId));

    // Fetch all data
    dispatch(fetchScreeningPackagesBulk(organizationId));
    dispatch(fetchAccreditationDefinitions(organizationId));
    dispatch(fetchOrganizationSettingsV2(organizationId));

    window.addEventListener('scroll', this.handleEquivScroll, true);
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleEquivScroll, true);
  }

  componentDidUpdate(prevProps) {
    const { dispatch, organizationId, isLoading } = this.props;

    // If organizationId just became available, fetch data
    if (!prevProps.organizationId && organizationId) {
      dispatch(fetchScreeningProviderSettings(organizationId));
      dispatch(fetchScreeningPackagesBulk(organizationId));
      dispatch(fetchAccreditationDefinitions(organizationId));
      dispatch(fetchOrganizationSettingsV2(organizationId));
    }

    // Once initial load completes, mark as no longer initial load
    if (prevProps.isLoading && !isLoading && this.state.isInitialLoad) {
      this.setState({ isInitialLoad: false });
    }

    // Show notice whenever newIcrFormEnabled becomes true (page load or toggle)
    if (!prevProps.newIcrFormEnabled && this.props.newIcrFormEnabled) {
      this.setState({ showIcrNotice: true });
    }
    // Sync equivalency rules from fetched org settings (only if no unsaved local changes)
    var prevSettings = prevProps.orgSettingsV2;
    var currSettings = this.props.orgSettingsV2;
    if (currSettings && currSettings !== prevSettings && !this.state.equivalencyDirty) {
      var rules = currSettings.accreditationEquivalencies || [];
      this.setState({ equivalencyRules: rules, equivalencyDirty: false });
    }
  }

  handleEquivScroll = () => {
    if (!this.equivHeaderRef.current) return;
    var rect = this.equivHeaderRef.current.getBoundingClientRect();
    var isHidden = rect.bottom < 0;
    if (isHidden !== this.state.showEquivFixedSave) {
      this.setState({ showEquivFixedSave: isHidden });
    }
  }

  handleTabChange = (key) => {
    this.setState({ activeTab: key });
  }

  handleAddEquivRule = () => {
    var rules = this.state.equivalencyRules.slice();
    rules.push({ has: undefined, satisfies: [] });
    this.setState({ equivalencyRules: rules, equivalencyDirty: true });
  }

  handleRemoveEquivRule = (index) => {
    var rules = this.state.equivalencyRules.slice();
    rules.splice(index, 1);
    this.setState({ equivalencyRules: rules, equivalencyDirty: true });
  }

  handleEquivHasChange = (index, value) => {
    var rules = this.state.equivalencyRules.slice();
    rules[index] = Object.assign({}, rules[index], { has: value });
    this.setState({ equivalencyRules: rules, equivalencyDirty: true });
  }

  handleEquivSatisfiesChange = (index, values) => {
    var rules = this.state.equivalencyRules.slice();
    rules[index] = Object.assign({}, rules[index], { satisfies: values });
    this.setState({ equivalencyRules: rules, equivalencyDirty: true });
  }

  handleSaveEquivalencies = () => {
    var rules = this.state.equivalencyRules;
    // Validate
    for (var i = 0; i < rules.length; i++) {
      if (!rules[i].has) {
        message.error('Each rule must have an accreditation type selected in "If constituent has".');
        return;
      }
      if (!rules[i].satisfies || rules[i].satisfies.length === 0) {
        message.error('Each rule must have at least one type in "Then skip these requirements".');
        return;
      }
    }

    var dispatch = this.props.dispatch;
    var organizationId = this.props.organizationId;

    // Convert to snake_case for the API
    var payload = rules.map(function(rule) {
      return { has: rule.has, satisfies: rule.satisfies };
    });

    this.setState({ equivalencySaving: true });
    dispatch(patchOrganizationSettingsV2(organizationId, { accreditation_equivalencies: payload }))
      .then(function() {
        // Clear flags but keep local rules — they match what was just saved.
        // Re-fetch settings so the Redux store (fetchOrganizationSettingsV2) is up to date.
        this.setState({ equivalencyDirty: false, equivalencySaving: false });
        dispatch(fetchOrganizationSettingsV2(organizationId));
      }.bind(this))
      .catch(function() {
        this.setState({ equivalencySaving: false });
      }.bind(this));
  }

  handleOpenModal = (pkg) => {
    const { groups } = this.props;

    // If creating a new package (pkg is null), open modal immediately
    if (!pkg) {
      this.setState({
        selectedPackage: null,
        selectedPackageGroups: [],
        showModal: true,
      });
      return;
    }

    // Find groups for this package from already-loaded groups data
    const packageGroupCodes = lodash.get(pkg, 'spChecks', []).map(check => check.type);
    const packageGroups = groups.filter(g => packageGroupCodes.includes(g.code));

    this.setState({
      selectedPackage: pkg,
      selectedPackageGroups: packageGroups,
      showModal: true,
    });
  }

  handleCloseModal = () => {
    this.setState({
      selectedPackage: null,
      selectedPackageGroups: [],
      showModal: false,
    });
  }

  handleOpenPreview = (pkg) => {
    const { groups } = this.props;

    // Find groups for this package from already-loaded groups data
    const packageGroupCodes = lodash.get(pkg, 'spChecks', []).map(check => check.type);
    const packageGroups = groups.filter(g => packageGroupCodes.includes(g.code));

    this.setState({
      previewPackage: pkg,
      previewPackageGroups: packageGroups,
    });
  }

  handleClosePreview = () => {
    this.setState({
      previewPackage: null,
      previewPackageGroups: [],
    });
  }

  // Helper to extract error messages from API response
  extractErrorMessages = (response) => {
    if (response && response.data && response.data._errors) {
      const errors = response.data._errors;
      const messages = [];
      Object.keys(errors).forEach(field => {
        const error = errors[field];
        if (error && error.msg) {
          messages.push(`${field}: ${error.msg}`);
        } else if (typeof error === 'string') {
          messages.push(`${field}: ${error}`);
        } else if (Array.isArray(error)) {
          messages.push(`${field}: ${error.join(', ')}`);
        }
      });
      return messages.length > 0 ? messages.join('; ') : null;
    }
    return null;
  }

  handleSavePackage = (packageData) => {
    const { dispatch, organizationId } = this.props;
    const { selectedPackage } = this.state;

    if (selectedPackage) {
      // Update existing package - keep modal open, don't refresh yet
      // (bulk refresh will happen after all saves complete via onAllSavesComplete)
      return dispatch(updateScreeningPackage(selectedPackage.id, packageData))
        .then((response) => {
          if (response && response.error) {
            const errorMsg = this.extractErrorMessages(response);
            message.error(errorMsg || 'Failed to update screening package');
            throw new Error('Update failed');
          } else {
            message.success('Screening package saved successfully');
            // Update the selected package with fresh data from response
            this.setState({ selectedPackage: response.data });
            return response;
          }
        })
        .catch((error) => {
          throw error;
        });
    } else {
      // Create new package - close modal after creation
      const data = {
        ...packageData,
        org_id: organizationId,
        package_requestor_id: 1,
      };

      return dispatch(createScreeningPackage(data))
        .then((response) => {
          if (response && response.error) {
            const errorMsg = this.extractErrorMessages(response);
            message.error(errorMsg || 'Failed to create screening package');
            throw new Error('Create failed');
          } else {
            // Check if package was reactivated
            const wasReactivated = response.data && response.data._meta && response.data._meta.reactivated;
            if (wasReactivated) {
              message.success('Package with same code already exists, re-activating.');
            } else {
              message.success('Screening package created successfully');
            }
            this.setState({ showModal: false, selectedPackage: null, selectedPackageGroups: [] });
            return dispatch(fetchScreeningPackagesBulk(organizationId));
          }
        })
        .catch((error) => {
          throw error;
        });
    }
  }

  handleCreateGroup = (groupData) => {
    const { dispatch, organizationId } = this.props;

    const data = {
      ...groupData,
      org_id: organizationId,
    };

    return dispatch(createScreeningGroup(data))
      .then((response) => {
        if (response && response.error) {
          const errorMsg = this.extractErrorMessages(response);
          message.error(errorMsg || 'Failed to create screening group');
          throw new Error('Failed to create screening group');
        }
        // Check if group was reactivated
        const wasReactivated = response.data && response.data._meta && response.data._meta.reactivated;
        if (wasReactivated) {
          message.success('Group with same code already exists, re-activating.');
        }
        // Refresh data to show new group
        return dispatch(fetchScreeningPackagesBulk(organizationId)).then(() => response);
      });
  }

  handleDeletePackage = (id, packageName) => {
    const { dispatch, organizationId } = this.props;

    Modal.confirm({
      title: 'Delete Screening Package',
      content: (
        <span>
          Are you sure you want to delete the package <strong>{packageName}</strong>?
          This action cannot be undone.
        </span>
      ),
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => {
        return dispatch(deleteScreeningPackage(id))
          .then(() => {
            message.success('Screening package deleted successfully');
            dispatch(fetchScreeningPackagesBulk(organizationId));
          })
          .catch(() => {
            message.error('Failed to delete screening package');
          });
      },
    });
  }

  handleUpdateGuidingText = (groupId, memberId, guidingText) => {
    const { dispatch } = this.props;
    const { selectedPackageGroups } = this.state;

    // Use selectedPackageGroups from state (lazy loaded) instead of groups from props
    return dispatch(updateMemberGuidingText(groupId, memberId, guidingText, selectedPackageGroups))
      .then((response) => {
        if (response && response.error) {
          throw new Error('Failed to update guiding text');
        }
        // Update the group in local state with the new guiding text
        const updatedGroups = selectedPackageGroups.map(group => {
          if (group.id === groupId) {
            return {
              ...group,
              screeningGroupMembers: (group.screeningGroupMembers || []).map(member => {
                if (member.id === memberId) {
                  return { ...member, guidingText: guidingText };
                }
                return member;
              }),
            };
          }
          return group;
        });
        this.setState({ selectedPackageGroups: updatedGroups });
        return response;
      });
  }

  handleUpdateGroupName = (groupId, newName) => {
    const { dispatch } = this.props;
    const { selectedPackageGroups } = this.state;

    // Use selectedPackageGroups from state (lazy loaded) instead of groups from props
    return dispatch(updateGroupName(groupId, newName, selectedPackageGroups))
      .then((response) => {
        if (response && response.error) {
          throw new Error('Failed to update group name');
        }
        // Update the group in local state with the new name
        const updatedGroups = selectedPackageGroups.map(group => {
          if (group.id === groupId) {
            return { ...group, name: newName };
          }
          return group;
        });
        this.setState({ selectedPackageGroups: updatedGroups });
        return response;
      });
  }

  handleUpdateGroupAttachments = (groupId, minCount, maxCount) => {
    const { dispatch } = this.props;
    const { selectedPackageGroups } = this.state;

    // Use selectedPackageGroups from state (lazy loaded) instead of groups from props
    return dispatch(updateGroupAttachments(groupId, minCount, maxCount, selectedPackageGroups))
      .then((response) => {
        if (response && response.error) {
          throw new Error('Failed to update attachment requirements');
        }
        // Update the group in local state with the new attachment settings
        const updatedGroups = selectedPackageGroups.map(group => {
          if (group.id === groupId) {
            return { ...group, minAttachmentCount: minCount, maxAttachmentCount: maxCount };
          }
          return group;
        });
        this.setState({ selectedPackageGroups: updatedGroups });
        return response;
      });
  }

  handleAllSavesComplete = () => {
    const { dispatch, organizationId } = this.props;
    const { selectedPackage } = this.state;

    // Refresh the package list (which also refreshes groups)
    // After bulk data is refreshed, the updated groups will be in props
    dispatch(fetchScreeningPackagesBulk(organizationId)).then(() => {
      // After refresh, update selectedPackageGroups with the latest group data
      if (selectedPackage && selectedPackage.id) {
        const updatedPackage = this.props.packages.find(pkg => pkg.id === selectedPackage.id);
        if (updatedPackage) {
          const packageGroupCodes = lodash.get(updatedPackage, 'spChecks', []).map(check => check.type);
          const packageGroups = this.props.groups.filter(g => packageGroupCodes.includes(g.code));
          this.setState({
            selectedPackage: updatedPackage,
            selectedPackageGroups: packageGroups,
          });
        }
      }
    });
  }

  handleCopyLink = (packageCode) => {
    const { organizationId, newIcrFormEnabled } = this.props;
    let baseDomain;
    if (newIcrFormEnabled) {
      const origin = window.location.origin;
      if (origin === 'http://localhost:3000') {
        baseDomain = 'http://localhost:5173';
      } else if (origin === 'https://staging.app.weareoho.com') {
        baseDomain = 'https://staging.app.icr.weareoho.com';
      } else if (origin === 'https://app.weareoho.com') {
        baseDomain = 'https://app.icr.weareoho.com';
      } else {
        baseDomain = origin;
      }
    } else {
      baseDomain = window.location.origin;
    }
    const link = baseDomain + '/oneoffchecks.html?a=icr_qr&o=' + organizationId + '&p=' + packageCode;

    navigator.clipboard.writeText(link).then(function() {
      message.success('Link copied to clipboard');
    }).catch(function() {
      message.error('Failed to copy link');
    });
  }

  handleExportConfig = () => {
    const { dispatch, organizationId, organization } = this.props;
    const orgName = organization && organization.name;

    dispatch(exportScreeningConfiguration(organizationId, orgName))
      .then(() => {
        message.success('Configuration exported successfully');
      })
      .catch(() => {
        message.error('Failed to export configuration');
      });
  }

  handleOpenImportModal = () => {
    this.setState({ showImportModal: true });
  }

  handleCloseImportModal = () => {
    this.setState({
      showImportModal: false,
      importFile: null,
      importPreview: null,
      importLoading: false,
    });
  }

  handleFileUpload = ({ file, onError, onSuccess }) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const content = event.target.result;
        const parsed = JSON.parse(content);

        // Validate structure (handle both camelCase and snake_case)
        if (!parsed.version) {
          message.error('Invalid file: missing version field');
          onError(new Error('Invalid file format'));
          return;
        }

        const screeningGroups = parsed.screeningGroups || parsed.screening_groups;
        const screeningPackages = parsed.screeningPackages || parsed.screening_packages;

        if (!screeningGroups || !Array.isArray(screeningGroups)) {
          message.error('Invalid file: missing or invalid screening_groups');
          onError(new Error('Invalid file format'));
          return;
        }
        if (!screeningPackages || !Array.isArray(screeningPackages)) {
          message.error('Invalid file: missing or invalid screening_packages');
          onError(new Error('Invalid file format'));
          return;
        }

        // Generate preview summary
        const preview = {
          groups: screeningGroups.length,
          packages: screeningPackages.length,
          sourceOrg: parsed.sourceOrgName || parsed.source_org_name || 'Unknown',
          exportedAt: parsed.exportedAt || parsed.exported_at || 'Unknown',
        };

        this.setState({
          importFile: content,
          importPreview: preview,
        });

        onSuccess();
      } catch (error) {
        message.error('Invalid JSON file format: ' + error.message);
        onError(error);
      }
    };
    reader.readAsText(file);
  }

  handleConfirmImport = () => {
    const { dispatch, organizationId } = this.props;
    const { importFile } = this.state;

    if (!importFile) {
      message.error('No file selected');
      return;
    }

    this.setState({ importLoading: true });

    dispatch(importScreeningConfiguration(organizationId, importFile))
      .then((response) => {
        const summary = response.data && response.data.summary;
        if (summary) {
          // Handle both camelCase and snake_case
          const groupsSummary = summary.screeningGroups || summary.screening_groups || {};
          const packagesSummary = summary.screeningPackages || summary.screening_packages || {};

          const groupsCreated = groupsSummary.created || 0;
          const groupsUpdated = groupsSummary.updated || 0;
          const packagesCreated = packagesSummary.created || 0;
          const packagesUpdated = packagesSummary.updated || 0;

          const totalCreated = groupsCreated + packagesCreated;
          const totalUpdated = groupsUpdated + packagesUpdated;

          const details = [];
          if (groupsCreated > 0) details.push(`${groupsCreated} group${groupsCreated !== 1 ? 's' : ''} created`);
          if (groupsUpdated > 0) details.push(`${groupsUpdated} group${groupsUpdated !== 1 ? 's' : ''} updated`);
          if (packagesCreated > 0) details.push(`${packagesCreated} package${packagesCreated !== 1 ? 's' : ''} created`);
          if (packagesUpdated > 0) details.push(`${packagesUpdated} package${packagesUpdated !== 1 ? 's' : ''} updated`);

          if (details.length > 0) {
            message.success(`Configuration imported successfully! ${details.join(', ')}`);
          } else {
            message.success('Configuration imported successfully! No changes were made.');
          }
        } else {
          message.success('Configuration imported successfully');
        }

        this.setState({
          showImportModal: false,
          importFile: null,
          importPreview: null,
          importLoading: false,
        });
      })
      .catch((error) => {
        const errorMsg = error && error.message ? error.message : 'Failed to import configuration';
        message.error(errorMsg);
        this.setState({ importLoading: false });
      });
  }

  handleClearAllPackages = () => {
    const { dispatch, organizationId } = this.props;

    Modal.confirm({
      title: 'Clear All Packages',
      content: (
        <div>
          <p>This will end-date all screening packages for this organization, making them inactive.</p>
          <p><strong>Are you sure you want to continue?</strong></p>
        </div>
      ),
      okText: 'Clear All',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => {
        return dispatch(clearAllScreeningPackages(organizationId))
          .then((response) => {
            if (response && response.error) {
              message.error('Failed to clear packages');
            } else {
              const count = response.data && response.data.count;
              message.success(`All packages have been cleared${count ? ` (${count} packages)` : ''}`);
              dispatch(fetchScreeningPackagesBulk(organizationId));
            }
          })
          .catch(() => {
            message.error('Failed to clear packages');
          });
      },
    });
  }

  handleConfigMenuClick = (e) => {
    switch (e.key) {
      case 'import':
        this.handleOpenImportModal();
        break;
      case 'export':
        this.handleExportConfig();
        break;
      case 'clear':
        this.handleClearAllPackages();
        break;
      default:
        break;
    }
  }

  handleSort = (column) => {
    const { sortColumn, sortDirection } = this.state;
    if (sortColumn === column) {
      // Toggle direction
      this.setState({
        sortDirection: sortDirection === 'asc' ? 'desc' : 'asc',
      });
    } else {
      // New column, default to ascending
      this.setState({
        sortColumn: column,
        sortDirection: 'asc',
      });
    }
  }

  getSortedPackages = (orgPackages) => {
    const { sortColumn, sortDirection } = this.state;

    // Check cache - return cached result if inputs haven't changed
    if (
      this.sortedPackagesCache.packages === orgPackages &&
      this.sortedPackagesCache.sortColumn === sortColumn &&
      this.sortedPackagesCache.sortDirection === sortDirection &&
      this.sortedPackagesCache.result
    ) {
      return this.sortedPackagesCache.result;
    }

    // Compute sorted packages
    const sorted = [...orgPackages].sort((a, b) => {
      let valueA, valueB;

      switch (sortColumn) {
        case 'name':
          valueA = (a.name || '').toLowerCase();
          valueB = (b.name || '').toLowerCase();
          break;
        case 'code':
          valueA = (a.code || '').toLowerCase();
          valueB = (b.code || '').toLowerCase();
          break;
        case 'groupCount':
          // Use groupCount from API if available, otherwise fall back to counting spChecks
          valueA = a.groupCount !== undefined ? a.groupCount : lodash.get(a, 'spChecks', []).length;
          valueB = b.groupCount !== undefined ? b.groupCount : lodash.get(b, 'spChecks', []).length;
          break;
        default:
          valueA = (a.name || '').toLowerCase();
          valueB = (b.name || '').toLowerCase();
      }

      if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;
      if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    // Update cache
    this.sortedPackagesCache = {
      packages: orgPackages,
      sortColumn: sortColumn,
      sortDirection: sortDirection,
      result: sorted,
    };

    return sorted;
  }

  renderSortIcon = (column) => {
    const { sortColumn, sortDirection } = this.state;
    if (sortColumn !== column) {
      return <Icon type="swap" style={{ marginLeft: '4px', color: '#bfbfbf', fontSize: '10px' }} />;
    }
    return (
      <Icon
        type={sortDirection === 'asc' ? 'caret-up' : 'caret-down'}
        style={{ marginLeft: '4px', color: '#1890ff', fontSize: '10px' }}
      />
    );
  }

  handleNewIcrFormToggle = checked => {
    const { dispatch, organizationId } = this.props;
    dispatch(patchOrganizationSettingsV2(organizationId, { newIcrFormEnabled: checked }))
      .then(() => dispatch(fetchOrganizationSettingsV2(organizationId)));
  }

  renderEquivalenciesTab() {
    var self = this;
    var rules = this.state.equivalencyRules;
    var equivalencyDirty = this.state.equivalencyDirty;
    var equivalencySaving = this.state.equivalencySaving;
    var showEquivFixedSave = this.state.showEquivFixedSave;
    var accreditationDefinitions = this.props.accreditationDefinitions || [];

    var options = accreditationDefinitions.map(function(def) {
      return (
        <Select.Option key={def.typeCode} value={def.typeCode}>
          {def.typeName + ' (' + def.typeCode + ')'}
        </Select.Option>
      );
    });

    return (
      <div className="equivalencies-tab">
        {showEquivFixedSave && equivalencyDirty && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
            backgroundColor: '#fff', borderBottom: '1px solid #e8e8e8',
            padding: '10px 24px', display: 'flex', justifyContent: 'flex-end',
            alignItems: 'center', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          }}>
            <span style={{ marginRight: '12px', color: '#faad14', fontSize: '13px' }}>
              You have unsaved changes
            </span>
            <Button type="primary" loading={equivalencySaving} onClick={this.handleSaveEquivalencies}>
              Save Changes
            </Button>
          </div>
        )}

        <div ref={this.equivHeaderRef} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h3 style={{ margin: 0 }}>Accreditation Equivalency Rules</h3>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button icon="plus" onClick={this.handleAddEquivRule}>Add Rule</Button>
            <Button
              type="primary"
              disabled={!equivalencyDirty || equivalencySaving}
              loading={equivalencySaving}
              onClick={this.handleSaveEquivalencies}
            >
              Save Changes
            </Button>
          </div>
        </div>
        <p style={{ color: '#666', marginBottom: '16px' }}>
          Define rules where holding one accreditation type satisfies other requirements.
          For example, if a person has an Australian Passport, they would not need a Visa check.
        </p>

        {rules.length === 0 ? (
          <p style={{ color: '#999', fontStyle: 'italic', marginBottom: '16px' }}>
            No equivalency rules configured. Click "Add Rule" to create one.
          </p>
        ) : (
          <table className="table table-striped" style={{ marginBottom: '16px' }}>
            <thead>
              <tr>
                <th style={{ width: '35%' }}>If constituent has</th>
                <th style={{ width: '45%' }}>Then skip these requirements</th>
                <th style={{ width: '20%', textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rules.map(function(rule, index) {
                return (
                  <tr key={index}>
                    <td>
                      <Select
                        style={{ width: '100%' }}
                        placeholder="Select accreditation type"
                        value={rule.has}
                        onChange={function(val) { self.handleEquivHasChange(index, val); }}
                        showSearch
                        filterOption={function(input, option) {
                          return option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                        }}
                      >
                        {options}
                      </Select>
                    </td>
                    <td>
                      <Select
                        mode="multiple"
                        style={{ width: '100%' }}
                        placeholder="Select types to skip"
                        value={rule.satisfies || []}
                        onChange={function(vals) { self.handleEquivSatisfiesChange(index, vals); }}
                        showSearch
                        filterOption={function(input, option) {
                          return option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                        }}
                      >
                        {options}
                      </Select>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <Tooltip title="Remove rule">
                        <Icon
                          type="delete"
                          onClick={function() { self.handleRemoveEquivRule(index); }}
                          style={{ fontSize: '16px', color: '#ff4d4f', cursor: 'pointer' }}
                        />
                      </Tooltip>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    );
  }

  renderPackagesList() {
    const { packages, groups, organizationId, newIcrFormEnabled } = this.props;
    const orgPackages = packages.filter(pkg => pkg.orgId === organizationId);
    const orgGroups = groups.filter(g => g.orgId === organizationId);
    const sortedPackages = this.getSortedPackages(orgPackages);

    const configMenu = (
      <Menu onClick={this.handleConfigMenuClick}>
        <Menu.Item key="import">
          <Icon type="cloud-upload" style={{ marginRight: '8px' }} />
          Import configuration file
        </Menu.Item>
        <Menu.Item key="export">
          <Icon type="cloud-download" style={{ marginRight: '8px' }} />
          Export configuration file
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item key="clear" style={{ color: '#ff4d4f' }}>
          <Icon type="delete" style={{ marginRight: '8px' }} />
          Clear all packages
        </Menu.Item>
      </Menu>
    );

    const sortableHeaderStyle = {
      cursor: 'pointer',
      userSelect: 'none',
    };

    return (
      <div className="screening-packages-list">
        <div className="list-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h3 style={{ margin: 0 }}>Screening Packages</h3>
            <Switch checked={newIcrFormEnabled} onChange={this.handleNewIcrFormToggle} size="small" />
            <span style={{ fontSize: '13px', color: '#666' }}>New ICR Form</span>
          </div>
          <div className="header-buttons">
            <Dropdown overlay={configMenu} trigger={['click']}>
              <Button style={{ marginRight: '8px' }}>
                <Icon type="setting" />
                Configuration
                <Icon type="down" />
              </Button>
            </Dropdown>
            <Button
              type="primary"
              onClick={() => this.handleOpenModal(null)}
            >
              Create New Package
            </Button>
          </div>
        </div>
        <div className="help-link" style={{ marginTop: '0', marginBottom: '8px' }}>
          <a
            href="https://help.weareoho.com/article/show/177317-setting-up-screening-packages"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#1890ff', textDecoration: 'none', fontSize: '13px' }}
          >
            <Icon type="question-circle" style={{ marginRight: '6px', fontSize: '13px' }} />
            Screening Package Instructions
          </a>
        </div>

        {orgPackages.length === 0 ? (
          <p>No screening packages found. Create your first package to get started.</p>
        ) : (
          <table className="table table-striped table-hover">
            <thead>
              <tr>
                <th style={{ width: '5%', textAlign: 'center' }}>Preview</th>
                <th
                  style={{ ...sortableHeaderStyle, width: '35%' }}
                  onClick={() => this.handleSort('name')}
                >
                  Package Name
                  {this.renderSortIcon('name')}
                </th>
                <th
                  style={{ ...sortableHeaderStyle, width: '25%' }}
                  onClick={() => this.handleSort('code')}
                >
                  Package Code
                  {this.renderSortIcon('code')}
                  <Tooltip title="This code is a unique identifier specific to the package that will show up in the URL of this screening package." overlayStyle={{ maxWidth: '250px' }}>
                    <Icon type="info-circle" style={{ marginLeft: '6px', color: '#000', fontSize: '12px' }} />
                  </Tooltip>
                </th>
                <th
                  style={{ ...sortableHeaderStyle, width: '20%' }}
                  onClick={() => this.handleSort('groupCount')}
                >
                  # of Accreditation Groups
                  {this.renderSortIcon('groupCount')}
                </th>
                <th style={{ width: '15%', textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedPackages.map(pkg => {
                // Use groupCount from API if available, otherwise fall back to counting spChecks
                const groupCount = pkg.groupCount !== undefined
                  ? pkg.groupCount
                  : lodash.get(pkg, 'spChecks', []).length;

                return (
                  <tr
                    key={pkg.id}
                    onClick={() => this.handleOpenModal(pkg)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td onClick={(e) => e.stopPropagation()} style={{ textAlign: 'center' }}>
                      <Tooltip title="Preview Package">
                        <Icon
                          type="eye"
                          onClick={() => this.handleOpenPreview(pkg)}
                          style={{
                            fontSize: '16px',
                            color: '#1890ff',
                            cursor: 'pointer',
                          }}
                        />
                      </Tooltip>
                    </td>
                    <td>
                      <span>{pkg.name}</span>
                      <Tooltip title="Copy package link">
                        <Icon
                          type="link"
                          onClick={(e) => {
                            e.stopPropagation();
                            this.handleCopyLink(pkg.code);
                          }}
                          style={{
                            fontSize: '14px',
                            color: '#52c41a',
                            cursor: 'pointer',
                            marginLeft: '8px',
                          }}
                        />
                      </Tooltip>
                    </td>
                    <td>{pkg.code}</td>
                    <td>{groupCount} {groupCount === 1 ? 'group' : 'groups'}</td>
                    <td onClick={(e) => e.stopPropagation()} style={{ textAlign: 'center' }}>
                      <Tooltip title="Edit">
                        <Icon
                          type="edit"
                          onClick={() => this.handleOpenModal(pkg)}
                          style={{
                            fontSize: '16px',
                            color: '#1890ff',
                            cursor: 'pointer',
                            marginRight: '12px',
                          }}
                        />
                      </Tooltip>
                      <Tooltip title="Delete">
                        <Icon
                          type="delete"
                          onClick={() => this.handleDeletePackage(pkg.id, pkg.name)}
                          style={{
                            fontSize: '16px',
                            color: '#ff4d4f',
                            cursor: 'pointer',
                          }}
                        />
                      </Tooltip>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    );
  }

  render() {
    const { providerSettings, isLoading, organizationId, packages, groups, accreditationDefinitions, accredFieldInfo, newIcrFormEnabled } = this.props;

    // Check if enrichment_requests_tracking is enabled
    const trackingValue = lodash.get(providerSettings, 'enrichmentRequestsTracking');
    const isFeatureEnabled = trackingValue === true || trackingValue === 'true';

    // Only show loading spinner on initial load, not on background refreshes
    if (isLoading && this.state.isInitialLoad) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <Spin size="large" tip="Loading screening packages..." />
        </div>
      );
    }

    if (!organizationId) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <Spin size="large" tip="Waiting for organization data..." />
        </div>
      );
    }

    // Filter packages and groups to only show those belonging to this organization
    const orgPackages = packages.filter(pkg => pkg.orgId === organizationId);
    const orgGroups = groups.filter(g => g.orgId === organizationId);

    return (
      <div className="screening-package-settings-page">
        <h2>Screening Package Settings</h2>
        <p className="page-description">
          Manage screening packages to collect accreditation information from candidates and/or new starters.
          Click any package to view or edit associated accreditation groups.
        </p>
        <div className="definitions-section" style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
          <div style={{ display: 'flex', marginBottom: '8px', fontSize: '13px' }}>
            <strong style={{ flexShrink: 0, width: '150px' }}>Screening package</strong>
            <span>A collection of accreditations and accreditation groups for someone to provide information for, bundled into one package (e.g. WWCC, Visa & Licence).</span>
          </div>
          <div style={{ display: 'flex', fontSize: '13px' }}>
            <strong style={{ flexShrink: 0, width: '150px' }}>Accreditation group</strong>
            <span>A single or set of accreditation types where information for only one type is required. For example proving Australian right-to-work status, someone can provide one of the following: a Visa, Birth Certificate or Australian Passport, if grouped in an accreditation group together.</span>
          </div>
        </div>

        <Tabs activeKey={this.state.activeTab} onChange={this.handleTabChange}>
          <Tabs.TabPane tab="Screening Packages" key="packages">
            {this.renderPackagesList()}
          </Tabs.TabPane>
          <Tabs.TabPane tab="Screening Groups" key="groups">
            <ScreeningGroupBrowser
              groups={groups}
              organizationId={organizationId}
              accreditationDefinitions={accreditationDefinitions}
              dispatch={this.props.dispatch}
              filterDynamicGroups={false}
            />
          </Tabs.TabPane>
          <Tabs.TabPane tab="Accreditation Equivalencies" key="equivalencies">
            {this.renderEquivalenciesTab()}
          </Tabs.TabPane>
        </Tabs>

        {/* Unified Package Edit Modal */}
        {this.state.showModal && (
          <PackageEditModal
            visible={this.state.showModal}
            onClose={this.handleCloseModal}
            onSave={this.handleSavePackage}
            onCreateGroup={this.handleCreateGroup}
            selectedPackage={this.state.selectedPackage}
            groups={orgGroups}
            allGroups={groups}
            packages={orgPackages}
            organizationId={organizationId}
            packageGroups={this.state.selectedPackageGroups}
            accreditationDefinitions={accreditationDefinitions}
            onUpdateGuidingText={this.handleUpdateGuidingText}
            onUpdateGroupName={this.handleUpdateGroupName}
            onUpdateGroupAttachments={this.handleUpdateGroupAttachments}
            onAllSavesComplete={this.handleAllSavesComplete}
          />
        )}

        {/* Package Preview Modal */}
        {this.state.previewPackage && (
          <PackagePreviewModal
            visible={!!this.state.previewPackage}
            onClose={this.handleClosePreview}
            selectedPackage={this.state.previewPackage}
            groups={this.state.previewPackageGroups}
            accreditationDefinitions={accreditationDefinitions}
            accredFieldInfo={accredFieldInfo || {}}
          />
        )}

        {/* Import Configuration Modal */}
        <Modal
          title="Import Screening Configuration"
          visible={this.state.showImportModal}
          onCancel={this.handleCloseImportModal}
          onOk={this.handleConfirmImport}
          okText="Import"
          okButtonProps={{
            disabled: !this.state.importFile,
            loading: this.state.importLoading
          }}
          width={600}
        >
          <div style={{ marginBottom: '20px' }}>
            <p style={{ marginBottom: '12px' }}>
              Upload a JSON file exported from another organization. This will import:
            </p>
            <ul style={{ marginLeft: '20px', marginBottom: '16px' }}>
              <li>Screening package names and their groups</li>
              <li>Screening group names, member types, and guiding text</li>
              <li>Attachment requirements (min/max counts)</li>
            </ul>
          </div>

          {!this.state.importFile ? (
            <Upload.Dragger
              accept=".json,application/json"
              multiple={false}
              customRequest={this.handleFileUpload}
              showUploadList={false}
            >
              <p className="ant-upload-drag-icon">
                <Icon type="inbox" />
              </p>
              <p className="ant-upload-text">Click or drag file to this area to upload</p>
              <p className="ant-upload-hint">Select a JSON configuration file</p>
            </Upload.Dragger>
          ) : (
            <div style={{
              padding: '16px',
              background: '#f5f5f5',
              borderRadius: '4px',
              marginBottom: '16px'
            }}>
              <p style={{ marginBottom: '0' }}>
                <Icon type="file" style={{ marginRight: '8px', color: '#1890ff' }} />
                <strong>File loaded successfully</strong>
              </p>
            </div>
          )}

          {this.state.importPreview && (
            <div style={{
              padding: '16px',
              background: '#e6f7ff',
              border: '1px solid #91d5ff',
              borderRadius: '4px',
              marginTop: '16px'
            }}>
              <h4 style={{ marginBottom: '12px', marginTop: '0' }}>
                <Icon type="info-circle" style={{ marginRight: '8px', color: '#1890ff' }} />
                Import Preview
              </h4>
              <p><strong>Source Organization:</strong> {this.state.importPreview.sourceOrg}</p>
              <p><strong>Exported:</strong> {new Date(this.state.importPreview.exportedAt).toLocaleString()}</p>
              <div style={{ marginTop: '12px' }}>
                <p><strong>This import will create or update:</strong></p>
                <ul style={{ marginLeft: '20px', marginTop: '8px', marginBottom: '0' }}>
                  <li>{this.state.importPreview.groups} screening group{this.state.importPreview.groups !== 1 ? 's' : ''}</li>
                  <li>{this.state.importPreview.packages} screening package{this.state.importPreview.packages !== 1 ? 's' : ''}</li>
                </ul>
              </div>
              <p style={{ marginTop: '12px', marginBottom: '0', color: '#666', fontSize: '12px' }}>
                <Icon type="check-circle" style={{ marginRight: '4px' }} />
                Existing items with matching codes will be updated. New items will be created.
              </p>
            </div>
          )}
        </Modal>

        <NewIcrFormNotice
          visible={this.state.showIcrNotice}
          onClose={() => this.setState({ showIcrNotice: false })}
        />

        <style>{`
          .screening-package-settings-page {
            padding: 20px;
          }

          .page-description {
            color: #666;
            margin-bottom: 20px;
          }

          .list-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
          }

          .header-buttons {
            display: flex;
            align-items: center;
          }

          .loading {
            text-align: center;
            padding: 40px;
            font-size: 18px;
          }

          .table {
            width: 100%;
            border-collapse: collapse;
          }

          .table th, .table td {
            padding: 12px;
            text-align: left;
            border-bottom: 1px solid #ddd;
          }

          .table-striped tbody tr:nth-child(even) {
            background-color: #f9f9f9;
          }

          .table-hover tbody tr:hover {
            background-color: #e9ecef;
          }

          /* ScreeningGroupAccordion styles */
          .screening-group-accordion .panel-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: 100%;
          }

          .screening-group-accordion .panel-title {
            display: flex;
            align-items: center;
          }

          .screening-group-accordion .panel-meta {
            color: #666;
            font-size: 12px;
          }

          .screening-group-members {
            padding: 10px 0;
          }

          .member-item {
            padding: 12px;
            margin-bottom: 12px;
            background-color: #fafafa;
            border-radius: 4px;
            border: 1px solid #f0f0f0;
          }

          .member-item:last-child {
            margin-bottom: 0;
          }

          .member-header {
            margin-bottom: 8px;
            display: flex;
            align-items: center;
          }

          .member-guiding-text {
            margin-left: 0;
          }
        `}</style>
      </div>
    );
  }
}

ScreeningPackageSettingsPage.propTypes = {
  dispatch: PropTypes.func.isRequired,
  organizationId: PropTypes.number.isRequired,
  organization: PropTypes.object,
  packages: PropTypes.array,
  groups: PropTypes.array,
  accreditationDefinitions: PropTypes.array,
  orgSettingsV2: PropTypes.object,
  providerSettings: PropTypes.object,
  isLoading: PropTypes.bool,
  newIcrFormEnabled: PropTypes.bool,
};

ScreeningPackageSettingsPage.defaultProps = {
  organization: null,
  packages: [],
  groups: [],
  accreditationDefinitions: [],
  orgSettingsV2: null,
  providerSettings: {},
  isLoading: false,
  newIcrFormEnabled: false,
};

function mapStateToProps(state) {
  const screeningPackages = state.doc.getIn(['screeningPackages']);
  const organizationSettings = state.doc.getIn(['http', 'fetchCurrentOrganization', 'data', 'settings', 'screening']) || {};

  // Helper to convert Immutable to JS, or return plain JS as-is
  const toPlainArray = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (value.toJS) return value.toJS();
    return [];
  };

  const toPlainObject = (value) => {
    if (!value) return {};
    if (typeof value === 'object' && !Array.isArray(value) && !value.toJS) return value;
    if (value.toJS) return value.toJS();
    return {};
  };

  const orgSettings = state.doc.getIn(['http', 'fetchOrganizationSettingsV2', 'data']);
  const newIcrFormEnabled = orgSettings ? !!orgSettings.get('newIcrFormEnabled') : false;

  return {
    organizationId: state.doc.getIn(['authMr', 'organizationId'], null),
    organization: toPlainObject(state.doc.getIn(['http', 'fetchCurrentOrganization', 'data'])),
    newIcrFormEnabled,
    packages: toPlainArray(screeningPackages && screeningPackages.get('packages')),
    groups: toPlainArray(screeningPackages && screeningPackages.get('groups')),
    accreditationDefinitions: toPlainArray(screeningPackages && screeningPackages.get('accreditationDefinitions')),
    accredFieldInfo: toPlainObject(screeningPackages && screeningPackages.get('accredFieldInfo')),
    orgSettingsV2: toPlainObject(state.doc.getIn(['http', 'fetchOrganizationSettingsV2', 'data'])),
    providerSettings: organizationSettings,
    isLoading: state.doc.getIn(['http', 'fetchScreeningPackagesBulk', 'inProgress'], false),
  };
}

export default connect(mapStateToProps)(ScreeningPackageSettingsPage);
